import React, { useMemo } from 'react';

import { Box, Flex, Grid, GridItem, HStack, RadioGroup, useTheme } from '@chakra-ui/react';
import { InputLabelTextWithInfoTooltip } from 'components/RabbitOKRCreationPanel/PrimaryPanel/TrackingSection/InputLabelTextWithInfoTooltip';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';

import { Calculator, InfoWorkZero, WeightageTracking } from 'assets/assetsV1/iconComponents';

import { GOAL_MEASUREMENT_TYPES, OKR_PROGRESS_TYPES } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { isMobileView } from 'constants/isMobileView';

import InputLabel from 'uiToolkitV1/InputLabel';
import { Radio } from 'uiToolkitV1/Radio';
import { SelectV2 } from 'uiToolkitV1/SelectV2';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

const TRACKING_TYPE = {
  VIA_MESH: {
    value: 'VIA_MESH',
    label: 'Via Mesh',
  },
  VIA_THIRD_PARTY: {
    value: 'VIA_THIRD_PARTY',
    label: 'Via Third party integrations',
  },
};

const OKRTrackingTypeSection = ({
  progressType,
  onChange,
  isReadOnly = true,
  okrHasNoChildren = true,
  areChildSummable = true,
  disableDependentType = false,
  disableDerivedAvg = false,
  parentMeasure,
  parentMeasureType,
  isTrackingByThirdPartyIntegrationsDisabled = false,
  hideTrackingByThirdPartyIntegrations = false,
  metricId = null,
}) => {
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const isTrackedByMesh = (progressType) =>
    [OKR_PROGRESS_TYPES.MANUAL, OKR_PROGRESS_TYPES.DERIVED_SUM, OKR_PROGRESS_TYPES.DERIVED_AVG].includes(progressType);

  const isTrackedByThirdParty = (progressType) => progressType === OKR_PROGRESS_TYPES.TRACKED;

  const handleTrackedByChange = (value) => {
    if (value === TRACKING_TYPE.VIA_MESH.value) {
      if (isTrackedByMesh(progressType)) return;
      onChange({ progressType: OKR_PROGRESS_TYPES.MANUAL });
    } else {
      onChange({ progressType: OKR_PROGRESS_TYPES.TRACKED });
    }
  };

  const selectedType = isTrackedByMesh(progressType) ? TRACKING_TYPE.VIA_MESH : TRACKING_TYPE.VIA_THIRD_PARTY;

  const isLinkedWithThirdPartyPluginMetric = metricId !== null;
  const isTrackingViaMeshDisabled = isTrackedByThirdParty(progressType) && isLinkedWithThirdPartyPluginMetric;
  const reasonForDisablingTrackByMesh = `This ${moduleName} is tracked using a third party plugin. Please unlink first to select 'Tracking Via Mesh'`;
  const mobileViewProps = isMobileView()
    ? {
        display: 'grid',
        gridTemplateColumns: '45% 45%',
        WebkitBoxPack: 'justify',
        WebkitJustifyContent: 'space-between',
        justifyContent: 'space-between',
        padding: '0',
        width: '100%',
      }
    : {};

  return (
    <>
      {hideTrackingByThirdPartyIntegrations ? null : (
        <InputLabel
          labelText={
            <InputLabelTextWithInfoTooltip
              // @ts-ignore
              labelText="Tracking Type"
              showInfoTooltip={!isReadOnly && isTrackingViaMeshDisabled}
              tooltipText={reasonForDisablingTrackByMesh}
            />
          }
          labelIcon={undefined}
          containerProps={{}}
        >
          <RadioGroup
            onChange={isReadOnly ? null : handleTrackedByChange}
            value={selectedType?.value}
            style={{ pointerEvents: isReadOnly ? 'none' : 'auto' }}
          >
            <HStack width="90%" justifyContent="space-between" py={2.5} px={3} {...mobileViewProps}>
              <Radio
                isDisabled={isTrackingViaMeshDisabled}
                isChecked={isTrackedByMesh(progressType)}
                value={TRACKING_TYPE.VIA_MESH.value}
                isReadOnly={isReadOnly}
                name={undefined}
                onChange={undefined}
              >
                <TextBodyMedium> {TRACKING_TYPE.VIA_MESH.label}</TextBodyMedium>
              </Radio>
              {hideTrackingByThirdPartyIntegrations ? null : (
                <Radio
                  isDisabled={isTrackingByThirdPartyIntegrationsDisabled}
                  isChecked={isTrackedByThirdParty(progressType)}
                  value={TRACKING_TYPE.VIA_THIRD_PARTY.value}
                  isReadOnly={isReadOnly}
                  name={undefined}
                  onChange={undefined}
                >
                  <TextBodyMedium> {TRACKING_TYPE.VIA_THIRD_PARTY.label}</TextBodyMedium>
                </Radio>
              )}
            </HStack>
          </RadioGroup>
        </InputLabel>
      )}

      {isTrackedByMesh(progressType) ? (
        <TrackedViaMesh
          progressType={progressType}
          onChange={onChange}
          isReadOnly={isReadOnly}
          okrHasNoChildren={okrHasNoChildren}
          areChildSummable={areChildSummable}
          disableDependentType={disableDependentType}
          disableDerivedAvg={disableDerivedAvg}
          parentMeasure={parentMeasure}
          parentMeasureType={parentMeasureType}
        />
      ) : null}
    </>
  );
};

export const isDependentType = (progressType) =>
  [OKR_PROGRESS_TYPES.DERIVED_SUM, OKR_PROGRESS_TYPES.DERIVED_AVG].includes(progressType);

const TrackedViaMesh = ({
  progressType,
  onChange,
  isReadOnly,
  okrHasNoChildren,
  areChildSummable,
  disableDependentType,
  disableDerivedAvg,
  parentMeasure,
  parentMeasureType,
}) => {
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const disabledDependentTypeTooltip = `Type, once changed to 'Independent', cannot be set to 'Dependent' as KR-KR cascading is disabled by your admin`;

  const TRACKED_VIA_MESH_DEPENDENT_TYPES = useMemo(
    () => ({
      DEPENDENT: {
        value: disableDerivedAvg ? OKR_PROGRESS_TYPES.DERIVED_SUM : OKR_PROGRESS_TYPES.DERIVED_AVG,
        label: 'Dependent',
        description: `Progress is derived as a function of the progress in immediate child ${modifyString(
          moduleName,
          StringModifier.PLURAL_SENTENCE_CASE
        )}`,
        isDisabled: disableDependentType,
      },
      INDEPENDENT: {
        value: OKR_PROGRESS_TYPES.MANUAL,
        label: 'Independent',
        description: `Progress is defined independently for this ${modifyString(
          moduleName,
          StringModifier.SENTENCE_CASE
        )}`,
      },
    }),
    [moduleName, disableDependentType, disableDerivedAvg]
  );

  const menuItems = [TRACKED_VIA_MESH_DEPENDENT_TYPES.DEPENDENT, TRACKED_VIA_MESH_DEPENDENT_TYPES.INDEPENDENT];

  const selectedItem = isDependentType(progressType)
    ? TRACKED_VIA_MESH_DEPENDENT_TYPES?.DEPENDENT
    : TRACKED_VIA_MESH_DEPENDENT_TYPES?.INDEPENDENT;

  const handleOnDependencyTypeChange = ({ value }) => {
    if (isDependentType(progressType) && isDependentType(value)) {
    } else {
      if (value === OKR_PROGRESS_TYPES.DERIVED_AVG) {
        onChange({
          progressType: value,
          measureType: 'Progress',
          measure: GOAL_MEASUREMENT_TYPES[1]?.code,
        });
        return;
      }
      if (value === OKR_PROGRESS_TYPES.DERIVED_SUM) {
        onChange({
          progressType: value,
          measureType: parentMeasureType,
          measure: parentMeasure,
        });
        return;
      }
      onChange({ progressType: value });
    }
  };

  return (
    <>
      <Grid gridTemplateColumns={isMobileView() ? '25% 4% 71%' : '20% 4% 76%'} alignItems="center" mb="16px">
        <GridItem>
          <InputLabelTextWithInfoTooltip
            // @ts-ignore
            labelText={<TextLabel color="text.g2">Tracking Type</TextLabel>}
            showInfoTooltip={disableDependentType}
            tooltipText={disabledDependentTypeTooltip}
          />
        </GridItem>
        {/* For spacing after first column */}
        <GridItem />
        <GridItem>
          <Box width="100%" whiteSpace="nowrap">
            <SelectV2
              isReadOnly={isReadOnly}
              menuItems={menuItems}
              selectedItem={selectedItem}
              renderControlItem={(menuItem) => (
                <Flex alignItems="center">
                  <WeightageTracking transform="rotate(0deg) !important" boxSize="18px" color="text.g2" fill="none" />
                  <TextBodyMedium ml="8px !important">{menuItem.label}</TextBodyMedium>
                </Flex>
              )}
              renderMenuItem={(menuItem) => (
                <Box>
                  <TextBodyMedium bold color="button.blue.hover">
                    {menuItem?.label}
                  </TextBodyMedium>
                  <TextLabel color="text.g2" whiteSpace="break-spaces">
                    {menuItem?.description}
                  </TextLabel>
                </Box>
              )}
              onSelect={handleOnDependencyTypeChange}
              menuListProps={{ maxWidth: '300px' }}
            />
          </Box>
        </GridItem>
      </Grid>
      {isDependentType(progressType) ? (
        <GridItem>
          <OKRTrackingFunction
            progressType={progressType}
            onChange={onChange}
            isReadOnly={isReadOnly}
            okrHasNoChildren={okrHasNoChildren}
            areChildSummable={areChildSummable}
            disableDerivedAvg={disableDerivedAvg}
            parentMeasure={parentMeasure}
            parentMeasureType={parentMeasureType}
          />
        </GridItem>
      ) : null}
    </>
  );
};

const NO_CHILDREN_ERROR_MESSAGE = (pluralLowerCaseModuleName, isDerivedSum) =>
  `No child ${pluralLowerCaseModuleName} to ${isDerivedSum ? 'sum' : 'average'}. Please set progress to 'Independent'`;

const InputLabelWithErrorOutline = React.memo(
  ({
    // @ts-ignore
    cannotChooseSum,
    // @ts-ignore
    cannotChooseAvg,
  }) => {
    const theme = useTheme();
    const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();
    const tooltipText = `Measure/Metric for all child ${modifyString(
      moduleName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} should be same to enable 'Dependant - Sum'`;
    const tooltipTextAvg = `'Dependant - Sum' parent ${modifyString(
      moduleName,
      StringModifier.SENTENCE_CASE
    )} cannot have 'Dependant - Average' child ${modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}`;

    return (
      <Tooltip fontSize="14px" label={cannotChooseSum ? tooltipText : cannotChooseAvg ? tooltipTextAvg : ''}>
        <Flex>
          <TextLabel color="text.g2">
            Computed as
            {cannotChooseSum || cannotChooseAvg ? (
              <InfoWorkZero style={{ marginLeft: '5px' }} boxSize="18px" color={theme.colors.neutral['666666']} />
            ) : null}
          </TextLabel>
        </Flex>
      </Tooltip>
    );
  }
);

const OKRTrackingFunction = ({
  progressType,
  onChange,
  isReadOnly,
  okrHasNoChildren,
  areChildSummable,
  disableDerivedAvg,
  parentMeasure,
  parentMeasureType,
}) => {
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const isSumAlreadySelected = progressType === OKR_PROGRESS_TYPES.DERIVED_SUM;
  const cannotChooseSum = !isReadOnly && !areChildSummable && !isSumAlreadySelected;
  const cannotChooseAvg = disableDerivedAvg;
  const menuItems = [
    {
      value: OKR_PROGRESS_TYPES.DERIVED_SUM,
      label: 'Sum',
      description: `Progress is computed as the total combined value of all immediate child ${modifyString(
        moduleName,
        StringModifier.PLURAL_SENTENCE_CASE
      )}`,
      isDisabled: cannotChooseSum,
    },
    {
      value: OKR_PROGRESS_TYPES.DERIVED_AVG,
      label: 'Average',
      description: `Progress is computed as the average value across all immediate child ${modifyString(
        moduleName,
        StringModifier.PLURAL_SENTENCE_CASE
      )}`,
      isDisabled: cannotChooseAvg,
    },
  ];

  const handleChange = ({ value }) => {
    if (value === OKR_PROGRESS_TYPES.DERIVED_AVG) {
      onChange({
        progressType: value,
        measureType: 'Progress',
        measure: GOAL_MEASUREMENT_TYPES[1]?.code,
      });
    } else {
      onChange({
        progressType: value,
        measureType: parentMeasureType,
        measure: parentMeasure,
      });
    }
  };

  const selectedItem = menuItems.find(({ value }) => value === progressType);

  return (
    // <InputLabel
    // // TODO: discuss with @sumangaurav-mesh and Priyam what this caption is about and do we need it
    //   captionText={
    //     okrHasNoChildren
    //       ? NO_CHILDREN_ERROR_MESSAGE(
    //           modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE),
    //           isSumAlreadySelected
    //         )
    //       : selectedItem?.value === OKR_PROGRESS_TYPES.DERIVED_SUM
    //   }
    //   captionVariant="error"
    // >
    <Grid gridTemplateColumns={isMobileView() ? '25% 4% 71%' : '20% 4% 76%'} alignItems="center" mb="16px">
      <GridItem>
        <InputLabelWithErrorOutline
          // @ts-ignore
          cannotChooseSum={cannotChooseSum}
          cannotChooseAvg={cannotChooseAvg}
        />
      </GridItem>
      {/* For spacing after first column */}
      <GridItem />
      <GridItem>
        <Box>
          <SelectV2
            isReadOnly={isReadOnly}
            menuItems={menuItems}
            selectedItem={selectedItem}
            renderControlItem={(menuItem) => (
              <Flex alignItems="center">
                <Calculator transform="rotate(0deg) !important" boxSize="18px" color="text.g2" fill="none" />
                <TextBodyMedium ml="8px !important">{menuItem.label}</TextBodyMedium>
              </Flex>
            )}
            renderMenuItem={(menuItem) => (
              <Box>
                <TextBodyMedium bold color="button.blue.hover">
                  {menuItem?.label}
                </TextBodyMedium>
                <TextLabel color="text.g2" whiteSpace="break-spaces">
                  {menuItem?.description}
                </TextLabel>
              </Box>
            )}
            onSelect={handleChange}
            menuListProps={{ maxWidth: '300px' }}
          />
        </Box>
      </GridItem>
    </Grid>
  );
};

export { OKRTrackingTypeSection };
